/**
 * Rendering a communication body that residents read.
 *
 * Bodies arrive in two shapes and both have to read like a letter from the community:
 *   - HTML  — what the server stores today: the rendered message with its paragraphs and
 *             links intact. Sanitised here before it is handed to dangerouslySetInnerHTML.
 *   - text  — SMS/WhatsApp copy, and every message stored before the server stopped
 *             flattening HTML to text. Rendered as HTML it would collapse into one run-on
 *             block with its links dead, so its line breaks are turned into real markup and
 *             bare URLs into real links.
 */
import { sanitizeMessageHtml } from '@shared/lib/sanitizeHtml';

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Trailing sentence punctuation is far more likely to be prose than part of the URL. */
const URL_PATTERN = /(https?:\/\/[^\s<]+[^\s<.,;:!?)"'])/g;

function linkify(escapedText: string): string {
  return escapedText.replace(
    URL_PATTERN,
    (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`,
  );
}

/** Blank line → new paragraph, single newline → line break. */
function textToParagraphs(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => `<p>${linkify(escapeHtml(block)).replace(/\n/g, '<br />')}</p>`)
    .join('');
}

/**
 * The HTML to render for a message body, whichever shape it arrived in.
 * Returns '' for an empty body so the caller can show its own empty state.
 */
export function toRenderableMessageHtml(message: string | null | undefined): string {
  if (!message) return '';
  const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(message);
  return looksLikeHtml ? sanitizeMessageHtml(message) : textToParagraphs(message);
}

/**
 * Typography for a rendered body. Tailwind's preflight zeroes every margin, so without
 * these the paragraph breaks the server preserves would still not be visible.
 */
export const MESSAGE_BODY_CLASS = [
  'text-body leading-7 text-ink',
  '[&_p]:mb-3 [&_p:last-child]:mb-0',
  '[&_h1]:mb-2 [&_h1]:font-display [&_h1]:text-headline [&_h1]:text-ink',
  '[&_h2]:mb-2 [&_h2]:font-display [&_h2]:text-body [&_h2]:font-semibold [&_h2]:text-ink',
  '[&_h3]:mb-2 [&_h3]:font-semibold [&_h3]:text-ink',
  '[&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5',
  '[&_li]:mb-1',
  '[&_a]:text-primary-700 [&_a]:underline [&_a]:break-words',
  '[&_strong]:font-semibold',
  '[&_img]:my-3 [&_img]:max-w-full [&_img]:rounded-md',
  '[&_hr]:my-4 [&_hr]:border-hairline',
].join(' ');
